import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Copy, Trash2, FileSignature } from "lucide-react";
import toast from "react-hot-toast";

import {
  getProposalTemplates, duplicateProposalTemplate, deleteProposalTemplate,
} from "../api/prospectingReportsApi";

const TEAL = "#0f8d9b";

const ProposalTemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getProposalTemplates({ scope: "all" })
      .then(({ data }) => setTemplates(data?.results || []))
      .catch(() => toast.error("Could not load templates."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const duplicate = async (t) => {
    try {
      await duplicateProposalTemplate(t.id);
      toast.success("Duplicated to an editable copy.");
      load();
    } catch (e) {
      toast.error("Could not duplicate it.");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove the "${t.name}" template?`)) return;
    try {
      await deleteProposalTemplate(t.id);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove it.");
    }
  };

  return (
    <div className="p-4 md:p-6">
      <button onClick={() => navigate("/prospecting/proposals")} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4">
        <ArrowLeft className="w-4 h-4" /> Proposals
      </button>
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-1">
        <FileSignature className="w-6 h-6" style={{ color: TEAL }} /> Proposal templates
      </h1>
      <p className="text-sm text-gray-500 mb-5">System templates are read-only. Duplicate one to make an editable agency version.</p>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl">
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                <p className="text-xs text-gray-500">{t.description}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${t.scope === "system" ? "bg-[#eaf1f3] text-[#0f8d9b]" : "bg-[#f2f3f5] text-[#68727d]"}`}>
                {t.scope}
              </span>
              <div className="flex items-center gap-1 ml-auto">
                <button onClick={() => duplicate(t)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900"><Copy className="w-3.5 h-3.5" /> Duplicate</button>
                {t.is_editable && (
                  <button onClick={() => remove(t)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProposalTemplatesPage;
